import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button as MuiButton,
  Typography,
} from "@mui/material";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import Student from "../images/Student.png";
import { FaEdit, FaTrash } from "react-icons/fa";
import authService from "../../../utils/authService";
import { fetchAllCourses, fetchCoursesByTrack } from "../../../utils/courseUtils";
import { fetchBatchesByCourse, fetchBatchesByCourseSingle, fetchBatchesByCoursesMulti } from "../../../utils/batchUtils";
import { fetchAllTracksForCourses, Track } from "../../../utils/trackUtils";
import MultiSelect, { OptionItem } from "../../../components/MultiSelect";

interface CollegeInfo {
  college_id: string;
  college_name: string;
}

interface CoordinatorData {
  id?: string;
  name: string;
  email: string;
  phone: string;
  type: "CO" | "SC";
  college_id: string; 
  del_row?: boolean;
}

const Coordinator: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const batchId = location.state?.batch_id ? location.state.batch_id : "";
  const batchName = location.state?.batch_name ? location.state.batch_name : "";
  const courseId = location.state?.course_id ? location.state.course_id : "";

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"new" | "edit">("new");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedCoordinator, setSelectedCoordinator] = useState<CoordinatorData | null>(null);
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [emailInput, setEmailInput] = useState<string>("");

  const [coordinators, setCoordinators] = useState<CoordinatorData[]>([]);
  const [colleges, setColleges] = useState<CollegeInfo[]>([]);
  const [courses, setCourses] = useState<{ course_id: string; course_name: string }[]>([]);
  const [batches, setBatches] = useState<{ batch_id: string; batch_name: string }[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>(courseId || "");
  const [selectedBatchesFilter, setSelectedBatchesFilter] = useState<string[]>(batchId ? [batchId] : []);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [selectedTrackForCO, setSelectedTrackForCO] = useState<string>("");
  const [multiCourses, setMultiCourses] = useState<string[]>([]);
  const [multiBatches, setMultiBatches] = useState<string[]>([]);
  const [combinedBatchesCO, setCombinedBatchesCO] = useState<{ batch_id: string; batch_name: string }[]>([]);
  const [modalCourses, setModalCourses] = useState<{ course_id: string; course_name: string }[]>([]);
  const [modalBatches, setModalBatches] = useState<{ batch_id: string; batch_name: string }[]>([]);
  const [allModalBatchesByCourse, setAllModalBatchesByCourse] = useState<Record<string, { batch_id: string; batch_name: string }[]>>({});
  const [selectedModalCourse, setSelectedModalCourse] = useState<string>("");
  const [selectedModalBatches, setSelectedModalBatches] = useState<string[]>([]);
  const [selectedTrackForSC, setSelectedTrackForSC] = useState<string>("");
  const [multiCoursesSC, setMultiCoursesSC] = useState<string[]>([]);
  const [multiBatchesSC, setMultiBatchesSC] = useState<string[]>([]);
  const [combinedBatchesSC, setCombinedBatchesSC] = useState<{ batch_id: string; batch_name: string }[]>([]);

  const [formData, setFormData] = useState<CoordinatorData>({
    id: undefined,
    name: "",
    email: "",
    phone: "",
    type: "SC",
    college_id: "",
  });

  const prevSelectedBatchesFilterRef = useRef<string[] | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchColleges(), fetchCoursesList(), fetchTracksList()])
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedCourse !== "") {
      fetchBatchesList(selectedCourse);
    } else {
      setBatches([]);
      setSelectedBatchesFilter([]);
    }
  }, [selectedCourse]);

  const fetchColleges = async () => {
    try {
      const response = await authService.get('admin/colleges/');
      setColleges(response.data.colleges || []);
    } catch (e) {
      console.error('Error fetching colleges', e);
    }
  };

  const fetchCoordinators = async (batchParam?: string[] | string) => {
    try {
      let payload: any = {};
      if (Array.isArray(batchParam)) {
        payload = { batch_ids: batchParam };
      } else if (batchParam) {
        payload = { batch_id: batchParam };
      } else {
        payload = { batch_ids: [] };
      }
      const response = await authService.post('admin/get_all_coordinator/', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      setCoordinators(response.data.coordinators || []);
    } catch (e) {
      console.error('Error fetching coordinators', e);
    }
  };

  useEffect(() => {
    const prevValue = prevSelectedBatchesFilterRef.current;
    const currentValue = selectedBatchesFilter;
    
    const hasChanged = prevValue === null || 
      prevValue.length !== currentValue.length ||
      prevValue.some((val, idx) => val !== currentValue[idx]);
    
    if (hasChanged) {
      prevSelectedBatchesFilterRef.current = currentValue;
      fetchCoordinators(selectedBatchesFilter);
    }
  }, [selectedBatchesFilter]);

  const fetchCoursesList = async () => {
    try {
      const data = await fetchAllCourses();
      setCourses(data);
    } catch (e) {
      console.error('Error fetching courses', e);
    }
  };

  const fetchBatchesList = async (course_id: string) => {
    try {
      const data = await fetchBatchesByCourse(course_id);
      setBatches(data);
    } catch (e) {
      console.error('Error fetching batches', e);
    }
  };

  const fetchTracksList = async () => {
    try {
      const data = await fetchAllTracksForCourses();
      setTracks(data);
    } catch (e) {
      console.error('Error fetching tracks', e);
    }
  };

  const fetchCoursesAndBatchesByCollege = async (college_id: string): Promise<{
    tracks: Track[];
    coursesByTrack: Record<string, { course_id: string; course_name: string }[]>;
    batchesByCourse: Record<string, { batch_id: string; batch_name: string }[]>;
  } | null> => {
    if (!college_id) {
      setTracks([]);
      setModalCourses([]);
      setAllModalBatchesByCourse({});
      setSelectedTrackForSC("");
      setMultiCoursesSC([]);
      setMultiBatchesSC([]);
      setCombinedBatchesSC([]);
      return null;
    }
    try {
      setLoading(true);
      const response = await authService.get(`admin/batch/get_batch_ids_by_college_id/${college_id}/`);
      const responseData = response.data || {};
      
      const tracksData = responseData.tracks || [];
      const formattedTracks = Array.isArray(tracksData) 
        ? tracksData.map((t: any) => ({
            track_id: String(t.track_id || t.id || ''),
            track_name: String(t.track_name || t.name || '')
          }))
        : [];
      
      const coursesData = responseData.courses || {};
      const coursesByTrack: Record<string, { course_id: string; course_name: string }[]> = {};
      
      Object.keys(coursesData).forEach((trackId) => {
        const trackCourses = coursesData[trackId];
        if (Array.isArray(trackCourses)) {
          coursesByTrack[trackId] = trackCourses.map((c: any) => ({
            course_id: String(c.course_id || c.id || ''),
            course_name: String(c.course_name || c.name || '')
          }));
        }
      });
      
      const batchesData = responseData.batches || {};
      const batchesByCourse: Record<string, { batch_id: string; batch_name: string }[]> = {};
      
      Object.keys(batchesData).forEach((courseId) => {
        const courseBatches = batchesData[courseId];
        if (Array.isArray(courseBatches)) {
          batchesByCourse[courseId] = courseBatches.map((b: any) => ({
            batch_id: String(b.batch_id || b.id || ''),
            batch_name: String(b.batch_name || b.name || '')
          }));
        }
      });
      
      setTracks(formattedTracks);
      setAllModalBatchesByCourse(batchesByCourse);
      setSelectedTrackForSC("");
      setMultiCoursesSC([]);
      setMultiBatchesSC([]);
      setCombinedBatchesSC([]);
      
      // Store data for use in dropdown handlers
      (window as any).__collegeData = { coursesByTrack, batchesByCourse };
      
      return { tracks: formattedTracks, coursesByTrack, batchesByCourse };
    } catch (e) {
      console.error('Error fetching courses and batches by college', e);
      setTracks([]);
      setModalCourses([]);
      setAllModalBatchesByCourse({});
      setSelectedTrackForSC("");
      setMultiCoursesSC([]);
      setMultiBatchesSC([]);
      setCombinedBatchesSC([]);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const onSelectTrackForCO = async (trackId: string) => {
    setSelectedTrackForCO(trackId);
    setMultiCourses([]);
    setMultiBatches([]);
    setCombinedBatchesCO([]);
    if (!trackId) return;
    try {
      const courseList = await fetchCoursesByTrack(trackId);
      setCourses(courseList);
    } catch (e) {
      console.error('Error fetching courses by track', e);
    }
  };

  const onSelectMultiCourses = async (values: string[]) => {
    setMultiCourses(values);
    setMultiBatches([]);
    if (values.length === 0) {
      setCombinedBatchesCO([]);
      return;
    }
    try {
      if (values.length === 1) {
        const single = await fetchBatchesByCourseSingle(values[0]);
        setCombinedBatchesCO(single);
      } else {
        const combined = await fetchBatchesByCoursesMulti(values);
        setCombinedBatchesCO(combined);
      }
    } catch (e) {
      console.error('Error fetching combined batches', e);
      setCombinedBatchesCO([]);
    }
  };

  const onSelectTrackForSC = async (trackId: string, coursesByTrack?: Record<string, { course_id: string; course_name: string }[]>) => {
    setSelectedTrackForSC(trackId);
    setMultiCoursesSC([]);
    setMultiBatchesSC([]);
    setCombinedBatchesSC([]);
    if (!trackId) {
      setModalCourses([]);
      return;
    }
    
    if (coursesByTrack && coursesByTrack[trackId]) {
      setModalCourses(coursesByTrack[trackId]);
    } else {
      try {
        const courseList = await fetchCoursesByTrack(trackId);
        setModalCourses(courseList);
      } catch (e) {
        console.error('Error fetching courses by track', e);
      }
    }
  };

  const onSelectMultiCoursesSC = async (values: string[], batchesByCourse?: Record<string, { batch_id: string; batch_name: string }[]>) => {
    setMultiCoursesSC(values);
    setMultiBatchesSC([]);
    if (values.length === 0) {
      setCombinedBatchesSC([]);
      return;
    }
    
    if (batchesByCourse) {
      const allBatches: { batch_id: string; batch_name: string }[] = [];
      values.forEach(courseId => {
        if (batchesByCourse[courseId]) {
          allBatches.push(...batchesByCourse[courseId]);
        }
      });
      setCombinedBatchesSC(allBatches);
    } else {
      try {
        if (values.length === 1) {
          const single = await fetchBatchesByCourseSingle(values[0]);
          setCombinedBatchesSC(single);
        } else {
          const combined = await fetchBatchesByCoursesMulti(values);
          setCombinedBatchesSC(combined);
        }
      } catch (e) {
        console.error('Error fetching combined batches', e);
        setCombinedBatchesSC([]);
      }
    }
  };

  const loadCoordinatorDataFromAPI = async (email: string) => {
    const tryParseArray = (s?: string) => {
      if (!s) return [] as string[];
      try {
        const parsed = JSON.parse(s);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [] as string[];
      }
    };
    try {
      setLoading(true);
      setError("");
      const response = await authService.post('/admin/get_user_by_email/', { email }, {
        headers: { 'Content-Type': 'application/json' },
      });
      const apiData = response?.data?.data || null;
      if (apiData) {
        const name = `${apiData.user_first_name || ''} ${apiData.user_last_name || ''}`.trim();
        const accessArr = tryParseArray(apiData.access);
        const inferredType = (apiData.category === 'CO' || accessArr.includes('CO')) ? 'CO' : 'SC';
        const collegeId = String(apiData.college_id || '');
        setFormData({
          id: apiData.user_id,
          name: name,
          email: apiData.user_email || email,
          phone: apiData.phone || '',
          type: inferredType as 'CO' | 'SC',
          college_id: collegeId,
        });
        
        setSelectedTrackForCO('');
        setMultiCourses([]);
        setMultiBatches([]);
        setCombinedBatchesCO([]);
        
        if (inferredType === 'SC' && collegeId) {
          try {
            const batchIds: string[] = Array.isArray(apiData.batch_ids) ? apiData.batch_ids : [];
            const trackIds: string[] = Array.isArray(apiData.track_ids) ? apiData.track_ids : [];
            const courseIds: string[] = Array.isArray(apiData.course_ids) ? apiData.course_ids : [];
            
            const result = await fetchCoursesAndBatchesByCollege(collegeId);
            
            if (result && trackIds.length > 0) {
              const { coursesByTrack, batchesByCourse } = result;
              const firstTrack = String(trackIds[0]);
              setSelectedTrackForSC(firstTrack);
              await onSelectTrackForSC(firstTrack, coursesByTrack);
              if (courseIds.length > 0) {
                await onSelectMultiCoursesSC(courseIds.map(String), batchesByCourse);
                setMultiBatchesSC(batchIds.map(String));
              }
            } else {
              setSelectedTrackForSC('');
              setMultiCoursesSC([]);
              setMultiBatchesSC([]);
              setCombinedBatchesSC([]);
            }
          } catch (e) {
            console.error('Error preloading SC selections', e);
          }
        }
      }
    } catch (e) {
      console.error('Error loading coordinator data', e);
      setError('Error loading coordinator information');
    } finally {
      setLoading(false);
    }
  };

  const handleModal = async (type: "new" | "edit", coordinator?: CoordinatorData) => {
    setModalType(type);
    setError("");
    if (type === 'new') {
      setFormData({ id: undefined, name: '', email: '', phone: '', type: 'SC', college_id: '' });
      setSelectedTrackForCO('');
      setMultiCourses([]);
      setMultiBatches([]);
      setCombinedBatchesCO([]);
      setModalCourses([]);
      setModalBatches([]);
      setAllModalBatchesByCourse({});
      setSelectedModalCourse("");
      setSelectedModalBatches([]);
      setSelectedTrackForSC('');
      setMultiCoursesSC([]);
      setMultiBatchesSC([]);
      setCombinedBatchesSC([]);
      setShowModal(true);
    } else if (coordinator) {
      const email = (coordinator as any).email || (coordinator as any).user_email || '';
      if (email) {
        await loadCoordinatorDataFromAPI(email);
        setShowModal(true);
      } else {
        const collegeId = String((coordinator as any).college_id ?? (coordinator as any).college ?? coordinator.college_id ?? '');
        setFormData({
          id: coordinator.id,
          name: coordinator.name,
          email: coordinator.email,
          phone: coordinator.phone,
          type: coordinator.type,
          college_id: collegeId,
        });
        setSelectedTrackForSC('');
        setMultiCoursesSC([]);
        setMultiBatchesSC([]);
        setCombinedBatchesSC([]);
        setShowModal(true);
      }
    }
  };

  const openEmailFirstFlow = () => {
    setEmailInput("");
    setShowEmailModal(true);
  };

  const handleEmailContinue = async () => {
    const trimmed = emailInput.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setError("Enter a valid email to continue");
      return;
    }
    setShowEmailModal(false);
    try {
      setLoading(true);
      const response = await authService.post('/admin/get_user_by_email/', { email: trimmed }, {
        headers: { 'Content-Type': 'application/json' },
      });
      const apiData = response?.data?.data || null;
      if (apiData) {
        setModalType('edit');
        await loadCoordinatorDataFromAPI(trimmed);
      } else {
        setModalType('new');
        setFormData({ id: undefined, name: '', email: trimmed, phone: '', type: 'SC', college_id: '' });
        setSelectedTrackForCO('');
        setMultiCourses([]);
        setMultiBatches([]);
        setCombinedBatchesCO([]);
        setModalCourses([]);
        setModalBatches([]);
        setSelectedModalCourse("");
        setSelectedModalBatches([]);
      }
      setShowModal(true);
    } catch (e) {
      // On API error, fallback to create with entered email
      console.error('Error checking user by email', e);
      setModalType('new');
      setFormData({ id: undefined, name: '', email: trimmed, phone: '', type: 'SC', college_id: '' });
      setSelectedTrackForCO('');
      setMultiCourses([]);
      setMultiBatches([]);
      setCombinedBatchesCO([]);
      setModalCourses([]);
      setModalBatches([]);
      setAllModalBatchesByCourse({});
      setSelectedModalCourse("");
      setSelectedModalBatches([]);
      setSelectedTrackForSC('');
      setMultiCoursesSC([]);
      setMultiBatchesSC([]);
      setCombinedBatchesSC([]);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const validate = (): string | null => {
    if (!formData.name || !formData.email || !formData.phone || !formData.type || !formData.college_id) {
      return "All fields are required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return "Invalid email format";
    }
    const digitsOnly = /^\d+$/;
    if (!digitsOnly.test(formData.phone)) {
      return "Phone should contain digits only";
    }
    if (formData.phone.length < 10 || formData.phone.length > 15) {
      return "Phone number must be 10 to 15 digits";
    }
    const targetEmail = formData.email || '';
    const duplicate = coordinators.some((c: any) => {
      const cEmail = c?.email || c?.user_email || '';
      const cId = c?.id || c?.user_id;
      const currentId = formData.id;
      return cEmail !== '' && cEmail === targetEmail && cId !== currentId;
    });
    if (duplicate) {
      return "Email already exists";
    }
    return null;
  };

  const handleSave = async (): Promise<void> => {
    const validationMessage = validate();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }
    setError("");
    setLoading(true);
    try {
      const json: any = { ...formData };
      
      if (formData.id) {
        json.user_id = formData.id;
      }
      delete json.id;
      
      if (formData.type === 'CO') {
        delete (json as any).batch_ids;
        delete (json as any).track_id;
        delete (json as any).course_ids;
      } else if (formData.type === 'SC') {
        json.batch_ids = multiBatchesSC;
        delete (json as any).track_id;
        delete (json as any).course_ids;
      }
      
      await authService.post('admin/save_coordinator/', json, {
        headers: { 'Content-Type': 'application/json' },
      });
      await fetchCoordinators(selectedBatchesFilter);
      setShowModal(false);
    } catch (e) {
      console.error('Error saving coordinator', e);
      setError('Error saving Coordinator');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (coordinator: CoordinatorData) => {
    setSelectedCoordinator(coordinator);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!selectedCoordinator) return;
    const userId = (selectedCoordinator as any).user_id || (selectedCoordinator as any).id || '';
    if (!userId) return;
    setLoading(true);
    try {
      const json = { user_id: userId };
      await authService.post('admin/delete_coordinator/', json, {
        headers: { 'Content-Type': 'application/json' },
      });
      await fetchCoordinators(selectedBatchesFilter);
      setShowDeleteModal(false);
    } catch (e) {
      console.error('Error deleting coordinator', e);
    } finally {
      setLoading(false);
    }
  };

  const collegeOptions = useMemo(() => colleges.map(c => ({ value: String(c.college_id), label: c.college_name })), [colleges]);

  return (
    <>
      {loading && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            display: 'flex', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)', alignItems: 'center', zIndex: 9999,
          }}
        >
          <Spinner animation="border" role="status" />
        </div>
      )}

      <div className="container-fluid p-0 pe-2" style={{ marginTop: "12px", height: `calc(100vh - 90px)` }}>
        <Card>
          <CardContent
            style={{
              height: `calc(100vh - 105px)`,
              overflow: "hidden",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
              style={{ flexShrink: 0 }}
            >
              <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                <FormControl
                  variant="outlined"
                  size="small"
                  style={{ minWidth: 140 }}
                >
                  <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                    Course
                  </InputLabel>
                  <Select
                    value={selectedCourse}
                    onChange={(e: SelectChangeEvent<string>) => {
                      setSelectedCourse(e.target.value);
                      setSelectedBatchesFilter([]);
                    }}
                    label="Course"
                    disabled={loading}
                    style={{
                      height: "36px",
                      fontSize: "14px",
                      fontFamily: "inherit",
                    }}
                  >
                    <MenuItem
                      value=""
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      Select course
                    </MenuItem>
                    {courses.map((course) => (
                      <MenuItem
                        key={course.course_id}
                        value={course.course_id}
                        style={{ fontSize: "14px", fontFamily: "inherit" }}
                      >
                        {course.course_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <div style={{ minWidth: '320px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography style={{ fontSize: "14px", fontFamily: "inherit" }}>
                    Batch(es)
                  </Typography>
                  <div style={{ flexGrow: 1 }}>
                    <MultiSelect
                      options={batches.map(b => ({ value: b.batch_id, label: b.batch_name }))}
                      value={selectedBatchesFilter}
                      onChange={setSelectedBatchesFilter}
                      placeholder="Select batches"
                      disabled={selectedCourse === ''}
                    />
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <MuiButton
                  variant="outlined"
                  size="small"
                  onClick={openEmailFirstFlow}
                  style={{
                    height: "36px",
                    fontSize: "12px",
                    textTransform: "none",
                    border: "1px solid #ccc",
                    color: "#666",
                  }}
                >
                  + Add Coordinator
                </MuiButton>
                <MuiButton
                  variant="contained"
                  size="small"
                  onClick={() => navigate('/admin/batches/coordinator/map-students', {
                    state: {
                      mappingFlow: true
                    }
                  })}
                  style={{
                    height: "36px",
                    fontSize: "12px",
                    textTransform: "none",
                  }}
                >
                  Mapping Coordinator
                </MuiButton>
              </div>
            </Box>

            <TableContainer
              component={Paper}
              style={{ 
                marginTop: "8px", 
                flexGrow: 1, 
                overflow: "auto",
                position: "relative"
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "1px solid #e0e0e0",
                        fontWeight: 600,
                        fontSize: "12px",
                        padding: "8px 12px",
                        minWidth: "80px",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      S.No
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "1px solid #e0e0e0",
                        fontWeight: 600,
                        fontSize: "12px",
                        padding: "8px 12px",
                        minWidth: "120px",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      Type
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "1px solid #e0e0e0",
                        fontWeight: 600,
                        fontSize: "12px",
                        padding: "8px 12px",
                        minWidth: "120px",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "1px solid #e0e0e0",
                        fontWeight: 600,
                        fontSize: "12px",
                        padding: "8px 12px",
                        minWidth: "120px",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      College
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "1px solid #e0e0e0",
                        fontWeight: 600,
                        fontSize: "12px",
                        padding: "8px 12px",
                        minWidth: "100px",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      Batch
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "1px solid #e0e0e0",
                        fontWeight: 600,
                        fontSize: "12px",
                        padding: "8px 12px",
                        minWidth: "80px",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      Count
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "1px solid #e0e0e0",
                        fontWeight: 600,
                        fontSize: "12px",
                        padding: "8px 12px",
                        minWidth: "150px",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      Email
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "1px solid #e0e0e0",
                        fontWeight: 600,
                        fontSize: "12px",
                        padding: "8px 12px",
                        minWidth: "120px",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      Mobile
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "1px solid #e0e0e0",
                        fontWeight: 600,
                        fontSize: "12px",
                        padding: "8px 12px",
                        minWidth: "120px",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coordinators.filter(c => !(c as any).del_row).map((coordinator: any, index: number) => {
                    const id = coordinator.id || coordinator.user_id || coordinator.email || coordinator.user_email;
                    const name = coordinator.name || `${coordinator.user_first_name || ''} ${coordinator.user_last_name || ''}`.trim();
                    const email = coordinator.email || coordinator.user_email || '';
                    const phone = coordinator.phone || '';
                    const typeText = coordinator.type === 'CO' ? 'College Admin' : 'College Coordinator';
                    const collegeName = collegeOptions.find(opt => opt.value === String(coordinator.college_id ?? coordinator.college))?.label || coordinator.college_name || coordinator.college || '';
                    const batchText = coordinator.batch || coordinator.batch_name || '-';
                    const countValue = coordinator.count !== undefined && coordinator.count !== null ? coordinator.count : '-';
                    return (
                      <TableRow
                        key={id}
                        style={{
                          backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f0f0f0",
                          },
                        }}
                      >
                        <TableCell
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#333",
                          }}
                        >
                          {index + 1}
                        </TableCell>
                        <TableCell
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#333",
                          }}
                        >
                          {typeText}
                        </TableCell>
                        <TableCell
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#333",
                          }}
                        >
                          {name}
                        </TableCell>
                        <TableCell
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#333",
                          }}
                        >
                          {collegeName}
                        </TableCell>
                        <TableCell
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#333",
                          }}
                        >
                          {batchText}
                        </TableCell>
                        <TableCell
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#333",
                          }}
                        >
                          {countValue}
                        </TableCell>
                        <TableCell
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#333",
                          }}
                        >
                          {email}
                        </TableCell>
                        <TableCell
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#333",
                          }}
                        >
                          {phone}
                        </TableCell>
                        <TableCell
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#333",
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            {coordinator.type === 'SC' && (
                              <img 
                                src={Student} 
                                title="Map Students" 
                                alt="Map Students" 
                                role="button" 
                                sizes="20" 
                                aria-label="Map Students" 
                                style={{ cursor: 'pointer', marginRight: '8px' }}
                                onClick={() => navigate('/admin/batches/coordinator/map-students', {
                                  state: {
                                    coordinator_id: coordinator.id || coordinator.user_id || '',
                                    coordinator_name: name,
                                    coordinator_email: email
                                  }
                                })}
                              />
                            )}
                            <FaEdit 
                              className="fs-6 me-2"
                              role="button" 
                              title="Edit" 
                              onClick={() => handleModal('edit', coordinator)} 
                              style={{ cursor: 'pointer' }}
                            />
                            <FaTrash 
                              className="fs-6 me-2"
                              role="button" 
                              title="Delete" 
                              onClick={() => handleDelete(coordinator)}
                              style={{ cursor: 'pointer' }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </div>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedCoordinator && (
              <p>Are you sure to delete?</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showModal} onHide={() => {
          setModalCourses([]);
          setShowModal(false);
        }} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{modalType === 'new' ? 'Create Coordinator' : 'Edit Coordinator'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <p className="text-danger">{error}</p>}
            <Form>
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Group controlId="formEmail">
                    <Form.Label>Email</Form.Label>
                    <Form.Control type="email" value={formData.email} disabled onChange={() => {}} />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="formCollege">
                    <Form.Label>College</Form.Label>
                    <Form.Select value={formData.college_id} onChange={async (e) => {
                      const collegeId = e.target.value;
                      setFormData({ ...formData, college_id: collegeId });
                      if (formData.type === 'SC' && collegeId) {
                        await fetchCoursesAndBatchesByCollege(collegeId);
                      } else {
                        setTracks([]);
                        if (formData.type === 'SC') {
                          setModalCourses([]);
                        }
                        setAllModalBatchesByCourse({});
                        setSelectedTrackForSC("");
                        setMultiCoursesSC([]);
                        setMultiBatchesSC([]);
                        setCombinedBatchesSC([]);
                      }
                    }}>
                      <option value="">Select College</option>
                      {collegeOptions.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group controlId="formType">
                    <Form.Label>Type</Form.Label>
                    <Form.Select value={formData.type} onChange={async (e) => {
                      const newType = e.target.value as "CO" | "SC";
                      setFormData({ ...formData, type: newType });
                      if (newType === 'SC') {
                        setSelectedTrackForCO('');
                        setMultiCourses([]);
                        setMultiBatches([]);
                        setCombinedBatchesCO([]);
                        setSelectedTrackForSC('');
                        setMultiCoursesSC([]);
                        setMultiBatchesSC([]);
                        setCombinedBatchesSC([]);
                        if (formData.college_id) {
                          await fetchCoursesAndBatchesByCollege(formData.college_id);
                        } else {
                          setTracks([]);
                          setModalCourses([]);
                          setAllModalBatchesByCourse({});
                        }
                      } else {
                        setSelectedTrackForSC('');
                        setMultiCoursesSC([]);
                        setMultiBatchesSC([]);
                        setCombinedBatchesSC([]);
                        setModalCourses([]);
                        setModalBatches([]);
                        setAllModalBatchesByCourse({});
                        setSelectedModalCourse("");
                        setSelectedModalBatches([]);
                        setTracks([]);
                        setModalCourses([]);
                      }
                    }}>
                      <option value="SC">College Coordinator</option>
                      <option value="CO">College Admin</option>
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="formName">
                    <Form.Label>Name</Form.Label>
                    <Form.Control type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group controlId="formPhone">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        const onlyDigits = e.target.value.replace(/\D/g, '');
                        setFormData({ ...formData, phone: onlyDigits });
                      }}
                      maxLength={15}
                    />
                  </Form.Group>
                </div>

                {formData.type === 'SC' && formData.college_id && (
                  <>
                    <div className="col-md-6">
                      <Form.Group controlId="formTrackSC">
                        <Form.Label>Track</Form.Label>
                        <Form.Select value={selectedTrackForSC} onChange={async (e) => {
                          const trackId = e.target.value;
                          const storedData = (window as any).__collegeData;
                          const coursesByTrack = storedData?.coursesByTrack;
                          await onSelectTrackForSC(trackId, coursesByTrack);
                        }}>
                          <option value="">Select Track</option>
                          {tracks.map(t => (
                            <option key={t.track_id} value={t.track_id}>{t.track_name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="formCoursesSC">
                        <Form.Label>Course(s)</Form.Label>
                        <MultiSelect
                          options={modalCourses.map<OptionItem>(c => ({ value: c.course_id, label: c.course_name }))}
                          value={multiCoursesSC}
                          onChange={async (values) => {
                            const storedData = (window as any).__collegeData;
                            const batchesByCourse = storedData?.batchesByCourse;
                            await onSelectMultiCoursesSC(values, batchesByCourse);
                          }}
                          disabled={!selectedTrackForSC}
                        />
                      </Form.Group>
                    </div>

                    <div className="col-md-6">
                      <Form.Group controlId="formBatchesSC">
                        <Form.Label>Batch(es)</Form.Label>
                        <MultiSelect
                          options={combinedBatchesSC.map<OptionItem>(b => ({ value: b.batch_id, label: b.batch_name }))}
                          value={multiBatchesSC}
                          onChange={setMultiBatchesSC}
                          disabled={multiCoursesSC.length === 0}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6" />
                  </>
                )}

              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setModalCourses([]);
              setShowModal(false);
            }}>Close</Button>
            <Button variant="primary" onClick={handleSave} disabled={loading}>{loading ? <Spinner animation="border" size="sm" /> : 'Save'}</Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Enter Coordinator Email</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <p className="text-danger">{error}</p>}
            <Form>
              <Form.Group controlId="preEmail" className="d-flex align-items-center justify-content-between">
                <Form.Label>Email</Form.Label>
                <Form.Control style={{ width: "60%" }} type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEmailModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleEmailContinue}>Continue</Button>
          </Modal.Footer>
        </Modal>
    </>
  );
};

export default Coordinator;


